import { useQuery } from '@tanstack/react-query'
import { stats } from '@/lib/api'
import { XboxIcon } from '@/components/PlatformIcons'

const XBOX_GREEN = '#107c10'

interface XboxGame {
  titleId: string
  name: string
  gamerscore: number
  maxGamerscore: number
  lastPlayed: string
  imageUrl?: string
}

interface XboxData {
  gamertag: string
  xuid?: string
  gamerscore: number
  avatarUrl: string
  accountTier?: string
  recentGames: XboxGame[]
}

export function XboxStats() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['stats', 'xbox'],
    queryFn: async () => {
      const result = await stats.getXbox()
      return result.data as unknown as XboxData
    },
  })

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#107c10] border-t-transparent mx-auto mb-4" />
          <p className="text-gray-400 text-sm">Loading Xbox stats...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="container mx-auto max-w-5xl px-4 py-16 text-center">
        <h2 className="text-2xl font-display font-bold mb-2">Failed to load Xbox stats</h2>
        <p className="text-gray-400 mb-6">Something went wrong fetching your Xbox data.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm font-medium transition-colors"
        >
          Try Again
        </button>
      </div>
    )
  }

  const allGames = data.recentGames || []
  const totalGamerscore = data.gamerscore || 0

  // Separate real Xbox games (with achievements) from PC/cross-play titles with no achievement data
  const xboxGames = allGames.filter(g => g.maxGamerscore > 0)
  const noAchievementGames = allGames.filter(g => g.maxGamerscore === 0 && g.gamerscore === 0)

  const gamesWithAchievements = xboxGames.length
  const achievementsEarned = xboxGames.reduce((sum, g) => sum + (g.gamerscore || 0), 0)
  const avgCompletion =
    gamesWithAchievements > 0
      ? Math.round(
          xboxGames.reduce((sum, g) => sum + (g.gamerscore / g.maxGamerscore) * 100, 0) / gamesWithAchievements,
        )
      : 0

  const sortedByProgress = [...xboxGames].sort((a, b) => b.gamerscore - a.gamerscore)
  const sortedByDate = [...xboxGames].sort(
    (a, b) => new Date(b.lastPlayed).getTime() - new Date(a.lastPlayed).getTime(),
  )

  return (
    <div className="container mx-auto max-w-5xl px-4 py-8 space-y-10">
      {/* Profile Header */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-[#107c10]/30 transition-colors p-6">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {data.avatarUrl ? (
            <img
              src={data.avatarUrl}
              alt={data.gamertag}
              className="w-20 h-20 rounded-full border-2 border-[#107c10]/50"
            />
          ) : (
            <div className="w-20 h-20 rounded-full border-2 border-[#107c10]/50 bg-gray-700 flex items-center justify-center">
              <XboxIcon size={28} color={XBOX_GREEN} />
            </div>
          )}
          <div className="flex-1 text-center sm:text-left">
            <div className="flex items-center justify-center sm:justify-start gap-2 mb-1">
              <XboxIcon size={18} color={XBOX_GREEN} />
              <h1 className="text-2xl font-display font-bold">{data.gamertag}</h1>
              {data.accountTier && (
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#107c10]/20 text-[#4ec94e]">
                  {data.accountTier}
                </span>
              )}
            </div>
            <p className="text-lg font-bold" style={{ color: XBOX_GREEN }}>
              {totalGamerscore.toLocaleString()} G
            </p>
          </div>
        </div>
      </div>

      {/* Top Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <BigStat label="Total Gamerscore" value={`${totalGamerscore.toLocaleString()} G`} />
        <BigStat label="Xbox Games" value={gamesWithAchievements.toString()} />
        <BigStat label="Achievements Earned" value={achievementsEarned.toLocaleString()} sub="gamerscore sum" />
        <BigStat label="Avg Completion" value={`${avgCompletion}%`} />
      </div>

      {/* Achievement Progress */}
      {sortedByProgress.length > 0 && (
        <section>
          <h2 className="text-xl font-display font-bold mb-4">Achievement Progress</h2>
          <div className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-[#107c10]/30 transition-colors divide-y divide-gray-800">
            {sortedByProgress.map((game) => {
              const pct = game.maxGamerscore > 0 ? Math.round((game.gamerscore / game.maxGamerscore) * 100) : 0
              return (
                <div key={game.titleId} className="flex items-center gap-4 p-3">
                  {game.imageUrl ? (
                    <img
                      src={game.imageUrl}
                      alt={game.name}
                      className="w-10 h-10 rounded object-cover shrink-0 bg-gray-700"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.visibility = 'hidden'
                      }}
                    />
                  ) : (
                    <div className="w-10 h-10 rounded bg-gray-700 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{game.name}</p>
                    <div className="mt-1 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${pct}%`, backgroundColor: XBOX_GREEN }}
                      />
                    </div>
                  </div>
                  <span className="text-sm text-gray-400 shrink-0 tabular-nums">
                    {game.gamerscore}/{game.maxGamerscore} G
                  </span>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Recent Games */}
      {sortedByDate.length > 0 && (
        <section>
          <h2 className="text-xl font-display font-bold mb-4">Recent Games</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {sortedByDate.slice(0, 6).map((game) => {
              const pct = Math.round((game.gamerscore / game.maxGamerscore) * 100)
              return (
                <div
                  key={game.titleId}
                  className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-[#107c10]/30 transition-colors overflow-hidden"
                >
                  {game.imageUrl ? (
                    <img
                      src={game.imageUrl}
                      alt={game.name}
                      className="w-full h-24 object-cover bg-gray-700"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.visibility = 'hidden'
                      }}
                    />
                  ) : (
                    <div className="w-full h-24 bg-gray-700 flex items-center justify-center">
                      <XboxIcon size={24} color="#4b5563" />
                    </div>
                  )}
                  <div className="p-3">
                    <p className="text-sm font-medium truncate mb-1">{game.name}</p>
                    <div className="flex items-baseline justify-between mb-1">
                      <span className="text-xs font-medium" style={{ color: XBOX_GREEN }}>
                        {game.gamerscore}/{game.maxGamerscore} G ({pct}%)
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">{formatDate(game.lastPlayed)}</p>
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Other Played Titles (no Xbox achievements) */}
      {noAchievementGames.length > 0 && (
        <section>
          <h2 className="text-xl font-display font-bold mb-1">Other Titles</h2>
          <p className="text-gray-500 text-sm mb-4">Played on Xbox but no achievement data available</p>
          <div className="flex flex-wrap gap-2">
            {noAchievementGames.map((game) => (
              <div
                key={game.titleId}
                className="flex items-center gap-2 bg-gray-800/50 rounded-lg border border-gray-800 px-3 py-2"
              >
                {game.imageUrl ? (
                  <img
                    src={game.imageUrl}
                    alt={game.name}
                    className="w-6 h-6 rounded object-cover bg-gray-700"
                    onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden' }}
                  />
                ) : (
                  <div className="w-6 h-6 rounded bg-gray-700 shrink-0" />
                )}
                <span className="text-sm text-gray-400">{game.name}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function BigStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-800 hover:border-[#107c10]/30 transition-colors p-5">
      <p className="text-2xl font-display font-bold mb-1">{value}</p>
      <p className="text-sm text-gray-400">{label}</p>
      {sub && <p className="text-xs text-gray-500 mt-0.5">{sub}</p>}
    </div>
  )
}

function formatDate(dateStr: string): string {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  } catch {
    return 'Recently'
  }
}
